import React, { useContext, useState } from 'react';
import { buildAuthHeaders, ensureCsrfToken } from '../utils/auth';
import { UserContext } from '../contexts/UserContext';
import { hasAdminAccess } from '../utils/permissionConfig';
import './AdminGuestRegistrationPage.css';

const AdminGuestRegistrationPage = () => {
  const { userInfo, isUserLoading } = useContext(UserContext);
  const [formData, setFormData] = useState({
    // 사용자 정보
    user_login_id: '',
    user_name: '',
    email: '',
    temp_password: '',
    color: '#cccccc',
    preferred_language: 'ko',

    // 게스트 프로필 정보
    instagram_id: '',
    guest_start_date: '',
    guest_end_date: '',
    commission_percentage: 0.00,
    commission_limit_amount: '',
    is_active: true,
    total_guest_visits: 0
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (isUserLoading) {
    return <div className="admin-guest-registration-page">사용자 정보를 불러오는 중...</div>;
  }

  if (!userInfo) {
    return <div className="admin-guest-registration-page">로그인이 필요합니다.</div>;
  }

  if (!hasAdminAccess(userInfo)) {
    return <div className="admin-guest-registration-page">관리자 권한이 필요합니다.</div>;
  }

  // 폼 데이터 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // 에러 메시지 초기화
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 실시간 검증
  const validateField = (name, value) => {
    switch (name) {
      case 'user_login_id':
        if (!value) return '로그인 아이디는 필수입니다.';
        if (value.length < 3) return '로그인 아이디는 최소 3자 이상이어야 합니다.';
        if (!/^[a-zA-Z0-9_]+$/.test(value)) return '로그인 아이디는 영문, 숫자, 언더스코어만 사용 가능합니다.';
        break;
      case 'user_name':
        if (!value) return '이름은 필수입니다.';
        break;
      case 'temp_password':
        if (!value) return '임시 비밀번호는 필수입니다.';
        if (value.length < 8) return '비밀번호는 최소 8자 이상이어야 합니다.';
        break;
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return '올바른 이메일 형식이 아닙니다.';
        }
        break;
      case 'guest_start_date':
        if (!value) return '게스트 시작일은 필수입니다.';
        break;
      case 'guest_end_date':
        if (!value) return '게스트 종료일은 필수입니다.';
        if (formData.guest_start_date && value < formData.guest_start_date) {
          return '게스트 종료일은 시작일 이전일 수 없습니다.';
        }
        break;
      case 'commission_percentage':
        const percentage = parseFloat(value);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          return '커미션 퍼센테이지는 0-100 사이의 값이어야 합니다.';
        }
        break;
      case 'commission_limit_amount':
        if (value && (isNaN(value) || parseInt(value) < 0)) {
          return '커미션 제한 금액은 0 이상의 숫자여야 합니다.';
        }
        break;
      case 'total_guest_visits':
        if (isNaN(value) || parseInt(value) < 0) {
          return '총 게스트 방문수는 0 이상의 숫자여야 합니다.';
        }
        break;
      default:
        break;
    }
    return '';
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    // 전체 폼 검증
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      await ensureCsrfToken({ forceRefresh: true });

      // 백엔드로 보낼 데이터 준비 - API 문서에 따라 temp_password 사용
      const requestData = {
        ...formData
      };

      const response = await fetch('/api/guest-artists/register', {
        method: 'POST',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json'
        }),
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '게스트 등록에 실패했습니다.');
      }

      // 응답 데이터 로깅
      console.log('게스트 등록 성공 응답:', data);

      // 성공 메시지에 로그인 정보와 게스트 기간 정보 포함
      const loginInfo = `게스트 아티스트가 성공적으로 등록되었습니다!
      
로그인 정보:
- 로그인 아이디: ${formData.user_login_id}
- 임시 비밀번호: ${formData.temp_password}
- 이메일: ${formData.email || '없음'}

게스트 기간 정보:
- 시작일: ${formData.guest_start_date}
- 종료일: ${formData.guest_end_date}
- 인스타그램: ${formData.instagram_id || '없음'}

게스트 아티스트가 이 정보로 로그인할 수 있습니다.`;

      setSuccessMessage(loginInfo);

      // 폼 초기화
      setFormData({
        user_login_id: '',
        user_name: '',
        email: '',
        temp_password: '',
        color: '#cccccc',
        preferred_language: 'ko',
        instagram_id: '',
        guest_start_date: '',
        guest_end_date: '',
        commission_percentage: 0.00,
        commission_limit_amount: '',
        is_active: true,
        total_guest_visits: 0
      });
      setErrors({});

    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-guest-registration-page">
      <div className="registration-container">
        <div className="registration-header">
          <h1>게스트 아티스트 등록</h1>
          <p>새로운 게스트 아티스트를 등록합니다.</p>
        </div>

        {successMessage && (
          <div className="success-message">
            <span>✅ {successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="error-message">
            <span>❌ {errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          {/* 사용자 정보 섹션 */}
          <div className="form-section">
            <h2>사용자 정보</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="user_login_id">로그인 아이디 *</label>
                <input
                  type="text"
                  id="user_login_id"
                  name="user_login_id"
                  value={formData.user_login_id}
                  onChange={handleInputChange}
                  className={errors.user_login_id ? 'error' : ''}
                  placeholder="예: guest_artist_1"
                />
                {errors.user_login_id && <span className="error-text">{errors.user_login_id}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="user_name">이름 *</label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleInputChange}
                  className={errors.user_name ? 'error' : ''}
                  placeholder="게스트 아티스트 이름"
                />
                {errors.user_name && <span className="error-text">{errors.user_name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">이메일</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="guest@example.com"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="temp_password">임시 비밀번호 *</label>
                <input
                  type="password"
                  id="temp_password"
                  name="temp_password"
                  value={formData.temp_password}
                  onChange={handleInputChange}
                  className={errors.temp_password ? 'error' : ''}
                  placeholder="최소 8자 이상"
                />
                {errors.temp_password && <span className="error-text">{errors.temp_password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="color">색상</label>
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferred_language">선호 언어 *</label>
                <select
                  id="preferred_language"
                  name="preferred_language"
                  value={formData.preferred_language}
                  onChange={handleInputChange}
                >
                  <option value="ko">한국어</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
          </div>

          {/* 게스트 프로필 정보 섹션 */}
          <div className="form-section">
            <h2>게스트 프로필 정보</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="instagram_id">인스타그램 ID</label>
                <input
                  type="text"
                  id="instagram_id"
                  name="instagram_id"
                  value={formData.instagram_id}
                  onChange={handleInputChange}
                  placeholder="@instagram_id"
                />
              </div>

              <div className="form-group">
                <label htmlFor="guest_start_date">게스트 시작일 *</label>
                <input
                  type="date"
                  id="guest_start_date"
                  name="guest_start_date"
                  value={formData.guest_start_date}
                  onChange={handleInputChange}
                  className={errors.guest_start_date ? 'error' : ''}
                />
                {errors.guest_start_date && <span className="error-text">{errors.guest_start_date}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="guest_end_date">게스트 종료일 *</label>
                <input
                  type="date"
                  id="guest_end_date"
                  name="guest_end_date"
                  value={formData.guest_end_date}
                  onChange={handleInputChange}
                  className={errors.guest_end_date ? 'error' : ''}
                />
                {errors.guest_end_date && <span className="error-text">{errors.guest_end_date}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="commission_percentage">커미션 퍼센테이지 (%)</label>
                <input
                  type="number"
                  id="commission_percentage"
                  name="commission_percentage"
                  value={formData.commission_percentage}
                  onChange={handleInputChange}
                  className={errors.commission_percentage ? 'error' : ''}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0.00"
                />
                {errors.commission_percentage && <span className="error-text">{errors.commission_percentage}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="commission_limit_amount">커미션 제한 금액</label>
                <input
                  type="number"
                  id="commission_limit_amount"
                  name="commission_limit_amount"
                  value={formData.commission_limit_amount}
                  onChange={handleInputChange}
                  className={errors.commission_limit_amount ? 'error' : ''}
                  min="0"
                  placeholder="0"
                />
                {errors.commission_limit_amount && <span className="error-text">{errors.commission_limit_amount}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="total_guest_visits">총 게스트 방문수</label>
                <input
                  type="number"
                  id="total_guest_visits"
                  name="total_guest_visits"
                  value={formData.total_guest_visits}
                  onChange={handleInputChange}
                  className={errors.total_guest_visits ? 'error' : ''}
                  min="0"
                  placeholder="0"
                />
                {errors.total_guest_visits && <span className="error-text">{errors.total_guest_visits}</span>}
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  계정 활성화
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? '등록 중...' : '게스트 아티스트 등록'}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => window.history.back()}
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminGuestRegistrationPage;
